import { isOneOf } from '@shared/helpers/array.helpers';
import { isProductCategoryElement } from '@shared/helpers/element-category.helpers';
import {
  isProjectFolder,
  isProjectInfo,
} from '@shared/helpers/project-folder.helpers';
import {
  getChildElements,
  getElementKind,
  isBuildingVersionElement,
  isElement,
  isProductElement,
} from '@shared/helpers/recursive-element.helpers';
import { ElementKind, OneOfListElements } from '@shared/models/project.model';
import { useIsElementExpanded } from '@client/hooks/use-expand-elements.hooks';
import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { useIsReadonlyElement } from '@client/hooks/use-user.hooks';
import { useUIState } from '@client/store/ui';

/**
 * Elements that can be dragged. Note that products are not draggable anymore
 */
const draggableKinds: ElementKind[] = [
  ElementKind.Element,
  ElementKind.ProjectFolder,
  ElementKind.ProjectInfo,
];

/**
 * All elements that can be dragged
 * @param element
 * @returns
 */
export const useIsDraggableElement = (
  element: OneOfListElements | undefined,
) => {
  const kind = getElementKind(element);
  const readonly = useIsReadonlyElement(element);
  const { renamingId } = useUIState('renamingId');

  // Can't drag if renaming something or in readonly mode
  if (renamingId || readonly) {
    return false;
  }

  return isOneOf(draggableKinds, kind);
};

interface IAllowedDropTypes {
  above: boolean;
  below: boolean;
  inside: boolean;
}
const dropTypeDefaults: IAllowedDropTypes = Object.freeze({
  above: false,
  below: false,
  inside: false,
});

export const useAllowedDropTypes = (
  element: OneOfListElements,
): IAllowedDropTypes => {
  const isExpanded =
    useIsElementExpanded(element) && getChildElements(element).length > 0;

  // These types can have children
  const isParent = isElement(element) || isProjectFolder(element);

  const dropTypes: IAllowedDropTypes = {
    inside: isParent && !isProductCategoryElement(element),
    below: !isExpanded,
    above: true,
  };

  // Unsupported types
  const unsupported =
    isProductElement(element) ||
    isBuildingVersionElement(element) ||
    isProjectInfo(element);

  return useObjectMemo(unsupported ? dropTypeDefaults : dropTypes);
};
